/**
 * Globs for policy patterns. Deliberately small and predictable:
 *
 *   *      any run of characters, spaces and slashes included ('**' is the same)
 *   ?      exactly one character
 *   x:*    Claude Code style prefix: 'x' alone, or 'x' followed by whitespace and anything
 *
 * Everything else is literal. Matching is anchored to the whole text.
 */
import { trimEndOf } from '@orgus/protocol';

const cache = new Map<string, RegExp>();
const CACHE_MAX = 2000;

function escape(ch: string): string {
  return /[\\^$.*+?()[\]{}|/-]/.test(ch) ? `\\${ch}` : ch;
}

function body(glob: string): string {
  let out = '';
  for (let i = 0; i < glob.length; i++) {
    const ch = glob[i]!;
    if (ch === '*') {
      while (glob[i + 1] === '*') i++;
      out += '.*';
    } else if (ch === '?') {
      out += '.';
    } else {
      out += escape(ch);
    }
  }
  return out;
}

export function globToRegExp(glob: string, ignoreCase = false): RegExp {
  const key = `${ignoreCase ? 'i' : 's'}${glob}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const src = glob.endsWith(':*') && glob.length > 2 ? `^${body(glob.slice(0, -2))}(?:\\s.*)?$` : `^${body(glob)}$`;
  const re = new RegExp(src, ignoreCase ? 'is' : 's');
  if (cache.size >= CACHE_MAX) cache.clear();
  cache.set(key, re);
  return re;
}

export function matchGlob(glob: string, text: string, ignoreCase = false): boolean {
  return globToRegExp(glob, ignoreCase).test(text);
}

/** Forward slashes, no trailing slash, drive letters lower-cased: for comparing paths. */
export function normalizePath(p: string): string {
  let s = p.replace(/\\/g, '/');
  if (/^[A-Za-z]:/.test(s)) s = s[0]!.toLowerCase() + s.slice(1);
  // Git Bash style /c/Users/... is the same folder as c:/Users/...
  const msys = /^\/([a-zA-Z])(\/.*)?$/.exec(s);
  if (msys) s = `${msys[1]!.toLowerCase()}:${msys[2] ?? '/'}`;
  // A loop, not /\/+$/ (quadratic in V8 on a long run of slashes that does not end the text).
  if (s.length > 1 && s.endsWith('/') && !/^[a-z]:\/$/.test(s)) s = trimEndOf(s, '/');
  return s;
}

export function isAbsolutePath(p: string): boolean {
  return /^([A-Za-z]:[\\/]|[\\/]|~)/.test(p);
}

/** Resolve '.' and '..' segments in a normalised path (no file system access). */
export function collapseDots(p: string): string {
  const abs = p.startsWith('/');
  const drive = /^[a-z]:/.exec(p)?.[0] ?? '';
  const rest = drive ? p.slice(drive.length) : p;
  const out: string[] = [];
  let escapes = 0;
  for (const seg of rest.split('/')) {
    if (!seg || seg === '.') continue;
    if (seg === '..') {
      if (out.length) out.pop();
      else escapes++;
      continue;
    }
    out.push(seg);
  }
  const lead = drive ? `${drive}/` : abs ? '/' : '../'.repeat(escapes);
  return `${lead}${out.join('/')}` || '.';
}

/** Is `p` (absolute, or relative to `dir`) inside `dir`? Case-insensitive on Windows-style paths. */
export function isInside(p: string, dir: string): boolean {
  const base = collapseDots(normalizePath(dir));
  let target = normalizePath(p);
  if (!isAbsolutePath(target)) target = `${base}/${target}`;
  target = collapseDots(target);
  const fold = /^[a-z]:/.test(base) || /^[a-z]:/.test(target);
  const a = fold ? target.toLowerCase() : target;
  const b = fold ? base.toLowerCase() : base;
  return a === b || a.startsWith(b.endsWith('/') ? b : `${b}/`);
}

/** `p` relative to `dir` when inside it, else null. */
export function relativeTo(p: string, dir: string): string | null {
  const base = collapseDots(normalizePath(dir));
  let target = normalizePath(p);
  if (!isAbsolutePath(target)) return collapseDots(target);
  target = collapseDots(target);
  if (!isInside(target, base)) return null;
  const rel = target.slice(base.length).replace(/^\/+/, '');
  return rel || '.';
}
